import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    completeClientSalesTask,
    createClientSalesNote,
    createClientSalesTask,
    fetchClientSalesActivity,
    updateClientSalesStatus,
} from "../../../../lib/api/clients";
import { isValidClientProfileId } from "../../../../lib/clientProfileId";

const SALES_STATUS_OPTIONS = [
    { code: "potential", label: "Потенциален" },
    { code: "active", label: "Активен" },
    { code: "risk", label: "Рисков" },
    { code: "inactive", label: "Неактивен" },
] as const;

export function useClient360SalesActivity(profileId: string) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ["client-sales-activity", profileId],
        queryFn: () => fetchClientSalesActivity(profileId),
        enabled: profileId !== "" && isValidClientProfileId(profileId),
    });

    const noteMutation = useMutation({
        mutationFn: (payload: { body: string; followUpAt?: string | null }) =>
            createClientSalesNote({
                profileId,
                body: payload.body,
                followUpAt: payload.followUpAt,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["client-sales-activity", profileId] });
        },
    });

    const taskMutation = useMutation({
        mutationFn: (payload: { title: string; dueAt?: string | null }) =>
            createClientSalesTask({
                profileId,
                title: payload.title,
                dueAt: payload.dueAt,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["client-sales-activity", profileId] });
        },
    });

    const completeTaskMutation = useMutation({
        mutationFn: (taskId: number) => completeClientSalesTask({ profileId, taskId }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["client-sales-activity", profileId] });
        },
    });

    const statusMutation = useMutation({
        mutationFn: (statusCode: string) =>
            updateClientSalesStatus({ profileId, statusCode }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["client-sales-activity", profileId] });
        },
    });

    return {
        query,
        noteMutation,
        taskMutation,
        completeTaskMutation,
        statusMutation,
        statusOptions: SALES_STATUS_OPTIONS,
    };
}
